import { Box, Button, Flex, HStack, Text, VStack } from '@chakra-ui/react';
import { useCallback, useState } from 'react';

import { EntryOption } from '../features/create/EntryOption';
import { OrDivider } from '../features/create/OrDivider';
import { BowerIcon } from '../ui/BowerIcon';
import { DropZone } from '../upload/intake/DropZone';
import { PROTOCOL_DOC } from '../upload/intake/rules';
import { useFileIntake } from '../upload/intake/useFileIntake';

/**
 * The two ways a structured protocol begins: a document read for you, or an
 * empty draft you write yourself.
 *
 * THE SECOND DOOR WAS CLOSED, AND THIS IS WHY IT IS OPEN AGAIN. "Start from
 * scratch" existed briefly and was removed, correctly: it created an empty
 * version and then stranded whoever made it, because the structured view had no
 * add-step control and the API had no create-step route - every structured
 * protocol arrived fully formed from derivation, and the edit routes exist to
 * CORRECT one (`PATCH /steps/:stepId` and friends all require a step that
 * already exists). Both halves exist now: `POST /:protocolId/steps`, and the
 * add controls on the page. The objection is spent, not overruled.
 *
 * THE CONFIRM STEP BELONGS TO THE FILE, not to the container. Dropping a file
 * or choosing one in an OS dialog is easy to do by accident and what follows is a long, paid
 * model turn, so the pick is shown back before anything runs. Starting from
 * scratch costs one empty row and is undone by deleting it, so it commits
 * immediately - a confirmation there would be ceremony over nothing.
 *
 * This component only ASKS. It creates nothing and knows nothing about
 * derivation: the Create modal's protocol step (`ProtocolBody`) owns the
 * create and the navigation.
 *
 * The file door is the shared `DropZone`, the same card every place that takes
 * a file uses; "Start from scratch" sits under it as the second way in.
 */

export interface CreateProtocolPanelProps {
  /** The user picked a source document and confirmed it. */
  readonly onFileChosen: (file: File) => void;
  /** The user chose to write the protocol themselves. */
  readonly onStartBlank: () => void;
  /** Disables both doors while the caller is creating. */
  readonly isBusy?: boolean;
}

/**
 * The picker and the file confirm step. Its pick resets when it unmounts, so a
 * container that unmounts it on close never shows the previous pick again.
 */
export function CreateProtocolPanel({
  onFileChosen,
  onStartBlank,
  isBusy = false,
}: CreateProtocolPanelProps) {
  const [chosenFile, setChosenFile] = useState<File | null>(null);

  // A drop and a pick both land on the confirm step below; nothing runs yet.
  const intake = useFileIntake({
    rule: PROTOCOL_DOC,
    surface: 'create_protocol',
    maxFiles: 1,
    readOnlyReason: isBusy ? 'Wait for this one to finish first.' : undefined,
    onFiles: ([file]) => setChosenFile(file),
  });

  const confirmFile = useCallback(() => {
    if (chosenFile) onFileChosen(chosenFile);
  }, [chosenFile, onFileChosen]);

  return (
    // A column: the content scrolls and the confirm row stays pinned below
    // it, so a host with a height cap (a short window) keeps it in view.
    <Flex direction="column" flex={1} minH={0}>
      {/* `p`/`m` keep focus rings inside the scroller's clip. */}
      <Box flex={1} minH={0} overflowY="auto" p={1} m={-1}>
        {chosenFile ? (
          <VStack align="stretch" spacing={4}>
            <Text fontSize="sm" color="text.secondary">
              Bower will read this document and lay its steps out as a structured protocol. You can
              correct anything it gets wrong afterwards.
            </Text>
            <HStack
              spacing={3}
              p={3}
              borderWidth="1px"
              borderColor="border.default"
              borderRadius="md"
              bg="bg.subtle"
            >
              <BowerIcon name="attachment" size="20px" />
              <Text fontSize="sm" color="text.primary" noOfLines={1}>
                {chosenFile.name}
              </Text>
            </HStack>
          </VStack>
        ) : (
          <VStack align="stretch" spacing={3}>
            <DropZone
              intake={intake}
              icon="upload_file"
              title="Drop a PDF or Word method, or click to choose"
              next="Bower reads the steps out of it. You can correct anything afterwards."
            />
            <OrDivider />
            {/* Second, because importing is the commoner errand and a
                procedure that exists on paper is the one people arrive with.
                Writing one from nothing is the deliberate choice, and a
                deliberate choice can afford to be the lower of two. */}
            <EntryOption
              icon={<BowerIcon name="protocol" size="20px" gradient="warm" />}
              title="Start from scratch"
              description="An empty protocol. Add the steps yourself as you go."
              onClick={onStartBlank}
              isDisabled={isBusy}
            />
          </VStack>
        )}
      </Box>
      {chosenFile && (
        <HStack justify="flex-end" spacing={3} pt={4} flexShrink={0}>
          <Button variant="ghost" onClick={() => setChosenFile(null)} isDisabled={isBusy}>
            Choose a different file
          </Button>
          <Button variant="primary" onClick={confirmFile} isLoading={isBusy}>
            Create protocol
          </Button>
        </HStack>
      )}
    </Flex>
  );
}
